import React, { useCallback, useEffect, useMemo } from 'react'
import classNames from 'classnames'
import { MeetingSummaryElementProps, MeetingResultElement } from '../../../models'
import { ResultCard, ResultCardPluginProps } from '../../../../MeetingResults/ResultCard/ResultCard'
import { useAppDispatch, useAppSelector } from '../../../../../../../../store/hooks'
import { selectSelectedMeetingTemplate } from '../../../../../../../../store/meetingSummary/meetingTemplates'
import { MeetingTemplatePresetView } from '../../../../../../../../views/Setup/MeetingTemplates/MeetingTemplateView'
import {
  registerMeetingSummaryEmbeddedAiResult,
  selectMeetingSummaryAgentEditedElements,
  selectMeetingSummaryAgentEditedLexicalState,
  selectMeetingSummaryAgentEditing,
  setMeetingSummaryEditing,
} from '../../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { convertMeetingSummaryElementToString } from '../../../utils'
import { MeetingSummaryInteractionType } from '../../../../../../../../models/MeetingSummary'
import { useMeetingSummary } from '../../../hooks'
import './EmbeddedMeetingResult.scss'
import './EmbeddedComponents.scss'

const EmbeddedMeetingResultPlugin: React.FC<ResultCardPluginProps> = ({ aiFeature, aiResult }) => {
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (!aiFeature || !aiResult) {
      return
    }
    const { id: aiFeatureId } = aiFeature
    const { id: aiResultId } = aiResult
    dispatch(registerMeetingSummaryEmbeddedAiResult({ aiFeatureId, aiResultId }))
  }, [dispatch, aiFeature, aiResult])

  return null
}

export const EmbeddedMeetingResult: React.FC<MeetingSummaryElementProps<MeetingResultElement>> = ({ element }) => {
  const dispatch = useAppDispatch()

  const { interactWithMeetingSummary } = useMeetingSummary()

  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const editing = useAppSelector(selectMeetingSummaryAgentEditing)
  const editedMeetingSummaryAgentLexicalState = useAppSelector(selectMeetingSummaryAgentEditedLexicalState)
  const editedElements = useAppSelector(selectMeetingSummaryAgentEditedElements)

  const {
    parameters: { resultTypeId },
  } = element

  const meetingTemplatePreset = selectedMeetingTemplate?.presets.find((preset) => preset.aiFeaturePresetId === resultTypeId)
  const { aiFeaturePresetId, aiFeatureId } = meetingTemplatePreset ?? {}

  const meetingTemplatePresetView: MeetingTemplatePresetView = useMemo(() => {
    return {
      aiFeaturePresetId: aiFeaturePresetId ?? '',
      aiFeatureId,
      row: 0,
      column: 0,
      colSpan: 1,
    }
  }, [aiFeaturePresetId, aiFeatureId])

  const removeAiFeaturePresetFromSummary = useCallback(async () => {
    if (!editedMeetingSummaryAgentLexicalState) {
      return
    }
    dispatch(setMeetingSummaryEditing(false))

    const newMeetingSummary = editedElements
      .filter((el) => el.displayId !== element.displayId)
      .map(convertMeetingSummaryElementToString)
      .join('')

    await interactWithMeetingSummary({ type: MeetingSummaryInteractionType.EDIT, newMeetingSummary })
  }, [dispatch, editedElements, editedMeetingSummaryAgentLexicalState, element.displayId, interactWithMeetingSummary])

  const extraHeaderOptions = useMemo(() => {
    return [
      {
        selectOption: { id: `remove-${aiFeaturePresetId}`, label: 'Remove from summary' },
        icon: 'bi-trash3',
        className: 'zocks-text-danger',
        handleSelected: removeAiFeaturePresetFromSummary,
      },
    ]
  }, [aiFeaturePresetId, removeAiFeaturePresetFromSummary])

  const headerResultOptionsConfig = useMemo(() => {
    return { hiddenOptions: ['edit'] }
  }, [])

  if (!resultTypeId || !meetingTemplatePreset) {
    return null
  }

  return (
    <div className={classNames('embedded-result-card py-2', { 'embedded-container': !editing, 'embedded-result-card-editing': editing })}>
      <ResultCard
        className='pt-0 pb-2'
        meetingTemplatePresetView={meetingTemplatePresetView}
        extraHeaderOptions={extraHeaderOptions}
        headerResultOptionsConfig={headerResultOptionsConfig}
        showEditButtons={false}
        plugins={[EmbeddedMeetingResultPlugin]}
      />
    </div>
  )
}
